import { useCallback, useMemo } from 'react'
import { Col, Container, Row, Spinner } from 'react-bootstrap'
import { useFormContext } from 'react-hook-form'
import { getIntegrationDisplayName, IntegrationServiceId, ZOCKS_EVENT_FIELDS } from '../../../../../../../common/integrations'
import { IntegrationIcon } from '../../../../../../../components/IntegrationIcon'
import { ReactComponent as ZocksIcon } from '../../../../../../../assets/ZocksIcon.svg'
import { SelectDropdown, SelectOption } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { InputWithTokens } from '../../../../../../../components/InputWithTokens/InputWithTokens'
import { InputWithTokensElements } from '../../../../../../../components/InputWithTokens/utils'
import { MentionData } from '../../../../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Nodes/MentionNode/MentionNode'
import { useAppSelector } from '../../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { useGetDescribeQuery } from '../../../../../../../store/integrations/integrations'
import { FormFieldDefinition } from '../../../hooks'
import { IntegrationSettings } from '../../../schema'

type CalendarMapping = NonNullable<IntegrationSettings['mappings']['calendar']>
type CalendarFields = NonNullable<CalendarMapping['fields']>
type FieldDescriptor = CalendarFields[string][number]

interface MapEventFieldsSectionProps {
  serviceId: IntegrationServiceId
}

export const MapEventFieldsSection: React.FC<MapEventFieldsSectionProps> = ({ serviceId }) => {
  const {
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext<IntegrationSettings>()
  const accountId = useAppSelector(selectAccountId)

  const eventFields = watch('mappings.calendar.fields')
  const eventObject = watch('mappings.calendar.objectName')

  const { data: describe, isFetching: isDescribeFetching } = useGetDescribeQuery(
    {
      accountId,
      connector: serviceId,
      context: { objectName: eventObject! },
    },
    { skip: !eventObject || !accountId },
  )

  const currentObjectFormFieldDefinitions: FormFieldDefinition[] = useMemo(() => describe?.data?.describe?.formFieldDefinitions || [], [describe])

  // Validates the edited attribute alone: `shouldValidate` on `mappings.calendar.fields` would flag every untouched required one.
  const setFieldDescriptors = useCallback(
    (zocksKey: string, descriptors: FieldDescriptor[] | null) => {
      const updatedFields: CalendarFields = { ...(eventFields ?? {}) }
      if (descriptors && descriptors.length > 0) {
        updatedFields[zocksKey] = descriptors
      } else {
        delete updatedFields[zocksKey]
      }
      setValue('mappings.calendar.fields', updatedFields, { shouldDirty: true })
      void trigger(`mappings.calendar.fields.${zocksKey}`)
    },
    [eventFields, setValue, trigger],
  )

  const onFieldSelected = useCallback(
    (zocksKey: string, option: SelectOption) => {
      const fieldDefinition = currentObjectFormFieldDefinitions.find((field) => field.name === option.id)
      setFieldDescriptors(zocksKey, [
        {
          type: 'field',
          value: option.id,
          valueType: fieldDefinition?.type ?? null,
          valueFormat: fieldDefinition?.extraTypeInfo ?? null,
        },
      ])
    },
    [currentObjectFormFieldDefinitions, setFieldDescriptors],
  )

  /**
   * A Zocks event attribute carries exactly one descriptor, so a mixed input collapses to the first token
   * (a `field` descriptor) or, when nothing was tokenized, to the typed text (a `static` descriptor).
   */
  const onInputWithTokensChange = useCallback(
    (elements: InputWithTokensElements[], zocksKey: string) => {
      const mention = elements.find((element) => element.type === 'mention' && element.mentionData)
      if (mention?.mentionData) {
        const metadata = mention.mentionData.metadata as { valueType?: string; valueFormat?: string } | undefined
        setFieldDescriptors(zocksKey, [
          { type: 'field', value: mention.mentionData.id, valueType: metadata?.valueType ?? null, valueFormat: metadata?.valueFormat ?? null },
        ])
        return
      }
      const staticValue = elements
        .filter((element) => element.type === 'text')
        .map((element) => element.content)
        .join('')
        .trim()
      setFieldDescriptors(zocksKey, staticValue ? [{ type: 'static', value: staticValue }] : null)
    },
    [setFieldDescriptors],
  )

  const fieldSelectOptions: SelectOption[] = useMemo(
    () => currentObjectFormFieldDefinitions.map((field) => ({ id: field.name, label: `${field.label} (${field.name})` })),
    [currentObjectFormFieldDefinitions],
  )

  const fieldSelectOptionsFor = (valueTypes: string[] | undefined): SelectOption[] => {
    if (!valueTypes) {
      return fieldSelectOptions
    }
    const allowedFieldNames = new Set(currentObjectFormFieldDefinitions.filter((field) => valueTypes.includes(field.type)).map((field) => field.name))
    return fieldSelectOptions.filter((option) => allowedFieldNames.has(option.id))
  }

  const inputTokenOptions: MentionData[] = useMemo(
    () =>
      currentObjectFormFieldDefinitions.map((fieldDefinition) => ({
        id: fieldDefinition.name,
        label: `${fieldDefinition.label} (${fieldDefinition.name})`,
        metadata: {
          valueType: fieldDefinition.type,
          valueFormat: fieldDefinition.extraTypeInfo || null,
        },
      })),
    [currentObjectFormFieldDefinitions],
  )

  const mapFieldDescriptorToInputWithTokensElements = (fieldDescriptors: FieldDescriptor[] | undefined): InputWithTokensElements[] => {
    if (!fieldDescriptors) {
      return []
    }
    return fieldDescriptors.map((descriptor) => {
      const { type, value } = descriptor
      if (type === 'static') {
        return { type: 'text', content: value ?? '' } as InputWithTokensElements
      }
      const fieldDefinition = currentObjectFormFieldDefinitions.find((field) => field.name === value)
      if (!fieldDefinition) {
        return { type: 'text', content: value ?? '' } as InputWithTokensElements
      }
      return {
        type: 'mention',
        content: `@${value}`,
        mentionData: { id: value ?? '', label: `${fieldDefinition.label} (${fieldDefinition.name})`, metadata: { ...fieldDefinition } },
      } as InputWithTokensElements
    })
  }

  if (currentObjectFormFieldDefinitions.length === 0 || isDescribeFetching) {
    return <Spinner />
  }

  return (
    <Container className='p-0'>
      <Row className='mb-3'>
        <Col>
          <h4>Map event fields</h4>
          <p className='zocks-text-gray-600'>
            Match your {getIntegrationDisplayName(serviceId)} {eventObject} fields to Zocks event fields so meetings land on the right calendar entry. Event
            type and Event subtype accept a fixed value as well, for objects that have no such field of their own. Leave Event type empty to sync events without
            a type. New filters won't be available on a fixed value or an empty Event type. Location is only used to find the meeting link. Leave Private empty
            on Event to use Salesforce's own Private flag.
          </p>
        </Col>
      </Row>

      <Row className='mb-2 fw-bold'>
        <Col className='d-flex align-items-center'>
          <IntegrationIcon serviceId={serviceId} width='1rem' height='1rem' fill='black' />
          <span className='ms-2'>{getIntegrationDisplayName(serviceId).toUpperCase()} FIELD</span>
        </Col>
        <Col className='d-flex align-items-center'>
          <ZocksIcon width='1rem' height='1rem' />
          <span className='ms-2'>ZOCKS FIELD</span>
        </Col>
      </Row>

      {ZOCKS_EVENT_FIELDS.map((mapping) => {
        const fieldDescriptors = eventFields?.[mapping.zocksKey]
        const fieldError = errors.mappings?.calendar?.fields?.[mapping.zocksKey]
        const selectOptions = fieldSelectOptionsFor(mapping.valueTypes)

        return (
          <Row key={mapping.zocksKey} className='mb-3 align-items-center'>
            <Col>
              {mapping.allowStaticValue ? (
                <InputWithTokens
                  options={inputTokenOptions}
                  onChange={(parsedContents) => onInputWithTokensChange(parsedContents, mapping.zocksKey)}
                  elements={mapFieldDescriptorToInputWithTokensElements(fieldDescriptors)}
                />
              ) : (
                <SelectDropdown
                  searchable
                  options={selectOptions}
                  initialSelectedOption={selectOptions.find((option) => option.id === fieldDescriptors?.[0]?.value) ?? null}
                  placeholder='Select field'
                  onOptionSelected={(option) => onFieldSelected(mapping.zocksKey, option)}
                  onClear={() => setFieldDescriptors(mapping.zocksKey, null)}
                  className='w-100'
                />
              )}
              {fieldError ? <div className='invalid-feedback d-block'>{fieldError.message || 'This field is required'}</div> : null}
            </Col>
            <Col className='d-flex align-items-center'>
              <i className='bi bi-arrow-right mx-3 zocks-text-gray-500 fs-5'></i>
              <span className='fw-medium'>
                {mapping.label}
                {mapping.required ? '*' : ''}
              </span>
            </Col>
          </Row>
        )
      })}
    </Container>
  )
}
